<script lang="ts" setup>
import { reactive, ref } from 'vue';
import SingleText from '@/views/Survey/Question/SingleText.vue';
import { useTranslate } from '@/locales/i18n';
import { AnswerModel, QuestionModel } from '../types';

const translate = useTranslate();

const props = defineProps<{
  question: QuestionModel;
  isChildQuestion?: boolean;
  multiQuestion?: boolean;
  errors?: any;
}>();
const emit = defineEmits(['answerChanged', 'childAnswerChanged']);

const data = reactive({
  model: props.question,
  isChildQuestion: props.isChildQuestion,
});
if (data.model.answer === undefined)
  data.model.answer = { id: props.question.id, answer: '', displayText: '' };

const options = props.question.multipleChoiceOptions?.filter((o) => o.hidden === false) ?? [];
if (props.question.showOtherOption) {
  options.push({
    label: translate('survey.questions.otherPleaseSpecify'),
    value: translate('survey.questions.other'),
    mode: 17,
    hidden: false,
    type: 'default',
  });
}

// Hold the answer locally because we want to pass in both the answer's value (mode) and the text answer for reporting purposes (label)
const answerLocal = ref('');

function modeSelected() {
  if (data.model.answer === undefined)
    data.model.answer = { id: props.question.id, answer: '', displayText: '' };
  const selectedOption = options.find((o) => o.label === answerLocal.value);
  data.model.answer.answer = selectedOption?.mode ?? 0;
  data.model.answer.displayText = selectedOption?.label;

  // Clear the "other" text input if a different option is selected
  if (data.model.childQuestions?.length &&
      selectedOption?.label !== translate('survey.questions.otherPleaseSpecify') &&
      data.model.childQuestions[0].answer) {
    data.model.childQuestions[0].answer.answer = '';
    emit('childAnswerChanged', data.model.childQuestions[0].answer);
  }

  emit('answerChanged', data.model.answer);
}

function childAnswerUpdated(answer: AnswerModel) {
  emit('childAnswerChanged', answer);
}
</script>

<template>
  <div v-if="data.model" :class="data.isChildQuestion ? 'mb-8' : 'mb-16'" class="text-left w-full">
    <p class="mb-2 font-bold whitespace-pre-line">
      {{ data.model.questionText }}
      <span v-if="data.model.mandatory && !data.isChildQuestion" class="text-red-500">*</span>
    </p>
    <p class="mb-4">{{ data.model.helperText }}</p>
    <template v-if="options.length">
      <label :for="data.model.id" class="mb-2 hidden">{{ data.model.questionText }}</label>
      <select
        :id="data.model.id"
        v-model="answerLocal"
        class="border bg-gray-50 rounded text-gray-700 py-2 px-3 focus:outline-none w-full inlineErrorInput"
        @change="modeSelected"
      >
        <option selected value="">{{ translate('survey.fields.pleaseSelect') }}</option>
        <option v-for="(option, index) in options" :key="index" :value="option.label">
          {{ option.label }}
        </option>
      </select>
    </template>

    <!-- If a child question exists, then it's the single text question for Other...  -->
    <div
      v-if="
        question.showOtherOption &&
        data.model.childQuestions?.length &&
        answerLocal == translate('survey.questions.otherPleaseSpecify')
      "
      class="mt-8"
    >
      <SingleText :question="data.model.childQuestions[0]" @answerChanged="childAnswerUpdated" />
    </div>
  </div>
</template>
